import type { ArticleBlock } from "@/lib/types/content";
import { parseCraftState, type CraftNodeMap } from "@/lib/craft/serialize";
import { parseHtmlToArticleBlocks } from "@/lib/craft/parse-pasted-content";
import { resolveHeadingId } from "@/lib/scroll-to-anchor";
import type { EditorBlock, EditorDoc, InlineContent } from "./doc-types";
import { emptyEditorDoc } from "./doc-types";

function textBlock(type: EditorBlock["type"], text: string, level?: number): EditorBlock {
  if (type === "heading" && level) {
    return { type: "heading", props: { level }, content: text };
  }
  if (type === "quote") {
    return { type: "quote", content: text };
  }
  return { type: "paragraph", content: text };
}

export function articleBlocksToDoc(blocks: ArticleBlock[]): EditorDoc {
  if (!blocks.length) return emptyEditorDoc();

  const doc: EditorBlock[] = [];
  for (const block of blocks) {
    if (block.type === "ul") {
      for (const item of block.items) {
        doc.push({ type: "bulletListItem", content: item });
      }
      continue;
    }
    if (block.type === "h2") {
      doc.push(textBlock("heading", block.text, 2));
    } else if (block.type === "h3") {
      doc.push(textBlock("heading", block.text, 3));
    } else {
      doc.push(textBlock("paragraph", block.text));
    }
  }
  return doc.length ? doc : emptyEditorDoc();
}

function craftTextToBlocks(state: CraftNodeMap): EditorBlock[] {
  const doc: EditorBlock[] = [];
  const rootIds = state.ROOT?.nodes ?? [];

  function walk(id: string) {
    const node = state[id];
    if (!node) return;
    const name = node.type?.resolvedName ?? "";

    if (name === "TextBlock") {
      const variant = String(node.props?.variant ?? "p");
      const text = String(node.props?.text ?? "").trim();
      if (!text) return;
      if (variant === "h2") doc.push({ type: "heading", props: { level: 2 }, content: text });
      else if (variant === "h3") doc.push({ type: "heading", props: { level: 3 }, content: text });
      else if (variant === "ul") {
        for (const line of text.split("\n")) {
          const item = line.replace(/^•\s*/, "").trim();
          if (item) doc.push({ type: "bulletListItem", content: item });
        }
      } else doc.push({ type: "paragraph", content: text });
      return;
    }

    if (name === "ImageBlock") {
      const url = String(node.props?.src ?? node.props?.imageUrl ?? "");
      if (url) {
        doc.push({
          type: "image",
          props: { url, caption: String(node.props?.caption ?? "") },
        });
      }
      return;
    }

    if (name === "QuoteBlock") {
      const text = String(node.props?.text ?? node.props?.quote ?? "").trim();
      if (text) doc.push({ type: "quote", content: text });
      return;
    }

    if (name === "ButtonLinkBlock") {
      doc.push({
        type: "ctaButton",
        props: {
          label: String(node.props?.label ?? node.props?.text ?? "Learn more"),
          href: String(node.props?.href ?? node.props?.url ?? "#"),
        },
      });
      return;
    }

    if (name === "VideoBlock" || name === "HtmlEmbedBlock") {
      const url = String(node.props?.url ?? node.props?.embedUrl ?? "");
      if (url) {
        doc.push({
          type: "embed",
          props: { url },
        });
      }
      return;
    }

    if (name === "DividerBlock") {
      doc.push({ type: "divider" });
      return;
    }

    for (const childId of node.nodes ?? []) walk(childId);
  }

  for (const id of rootIds) walk(id);
  return doc;
}

export function craftStateToDoc(craftState: string | object | null | undefined): EditorDoc {
  const state = parseCraftState(craftState);
  if (!state?.ROOT?.nodes?.length) return emptyEditorDoc();
  const doc = craftTextToBlocks(state);
  return doc.length ? doc : emptyEditorDoc();
}

export function htmlToDoc(html: string): EditorDoc {
  const trimmed = html?.trim();
  if (!trimmed) return emptyEditorDoc();
  return articleBlocksToDoc(parseHtmlToArticleBlocks(trimmed));
}

export function resolveEditorDocForLoad(input: {
  editorDoc?: unknown;
  craftState?: unknown;
  contentBlocks?: ArticleBlock[] | null;
  contentHtml?: string | null;
}): EditorDoc {
  if (Array.isArray(input.editorDoc) && input.editorDoc.length > 0) {
    return input.editorDoc as EditorDoc;
  }
  if (input.craftState) {
    const fromCraft = craftStateToDoc(input.craftState as string | object);
    const state = parseCraftState(input.craftState as string | object);
    if (state?.ROOT?.nodes?.length) return fromCraft;
  }
  if (input.contentBlocks?.length) return articleBlocksToDoc(input.contentBlocks);
  if (input.contentHtml?.trim()) return htmlToDoc(input.contentHtml);
  return emptyEditorDoc();
}

function inlineText(content: InlineContent | undefined): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object" && "text" in item) return String(item.text ?? "");
      return "";
    })
    .join("");
}

export function docPlainText(doc: EditorDoc | null | undefined): string {
  if (!doc?.length) return "";
  const parts: string[] = [];
  for (const block of doc) {
    const type = block.type ?? "paragraph";
    if (type === "bulletListItem" || type === "numberedListItem") {
      parts.push(inlineText(block.content));
    } else if (type === "heading" || type === "paragraph" || type === "quote") {
      parts.push(inlineText(block.content));
    } else if (type === "callout") {
      parts.push(String((block.props as { text?: string })?.text ?? ""));
    } else if (type === "ctaButton") {
      parts.push(String((block.props as { label?: string })?.label ?? ""));
    } else if (type === "image") {
      parts.push(String((block.props as { caption?: string })?.caption ?? ""));
    }
  }
  return parts.filter(Boolean).join(" ");
}

export function docHeadingId(text: string, props?: Record<string, unknown>): string {
  const anchor = props?.anchorId;
  if (typeof anchor === "string" && anchor) return anchor;
  return resolveHeadingId(text);
}
